/**
 * Per-channel rotation controllers for the audio bus, extracted verbatim from
 * use-playback so the selection logic is unit-testable without a DOM.
 *
 * Each function is pure over its inputs apart from the explicitly-passed
 * mutable rotation state (the same pattern player-core's `nextRotationItem`
 * uses): the hook owns the state in refs and hands it in per call, so the
 * decision core carries no React or media-element dependency.
 */

import type { DeviceMediaItem, DeviceSync } from "@workspace/api-client-react";
import {
  anchorRotationIndex,
  buildRotation,
  channelSlot,
  createRotationAnchor,
  isSlotActive,
  makeEntry,
  mediaAssetById,
  nextRotationItem,
  playlistById,
  syncShuffleBase,
  type LocalRotationState,
  type QueueEntry,
  type RotationAnchor,
} from "@workspace/player-core";
import {
  alignMusicToTimeline,
  hasSyncTimeline,
  type TimelineAlignment,
} from "./sync-group.ts";
import {
  readAnnouncementTone,
  resolveToneUrl,
  shouldPrependTone,
  toneQueueEntry,
} from "./announcement-tone.ts";

/**
 * Mutable rotation state for the music channel. The rotation array's exact
 * order is load-bearing for multi-room sync-group alignment (the server
 * segment timeline materializes the identical sequence — see CB-015 and
 * `alignMusicRotationToTimeline` below), so it is anchored — never reordered —
 * to the server-resolved item.
 */
export interface MusicRotationState {
  rotation: DeviceMediaItem[];
  index: number;
  playlistId: string | null;
  anchor: RotationAnchor;
}

export function createMusicRotationState(): MusicRotationState {
  return {
    rotation: [],
    index: 0,
    playlistId: null,
    anchor: createRotationAnchor(),
  };
}

/**
 * Pull the next scheduled music entry, walking (and lazily rebuilding) the
 * materialized rotation in `state`. CB-015: prefers the server's resolved
 * rotation pick for this slot occupancy (once) so playback matches
 * `/schedule/preview` and `/schedule/resolve` — skipped for multi-room
 * sync-group devices, whose index is already positioned by
 * `alignMusicRotationToTimeline`. The timeline is authoritative there by
 * design: its segment math derives the SAME mode/weight-aware sequence as
 * `buildRotation` (player-core materializeRotation, same seed), so deferring
 * to slot.itemId as well would only fight the group alignment.
 */
export function takeNextMusicEntry(
  sync: DeviceSync | null,
  state: MusicRotationState,
  now: Date = new Date(),
): QueueEntry | null {
  const slot = channelSlot(sync, "music");
  if (!isSlotActive(slot)) return null;

  if (slot?.mediaAssetId) {
    const item = mediaAssetById(sync, slot.mediaAssetId);
    if (!item?.url) return null;
    return makeEntry(item, "music", slot, null, false);
  }

  const playlist = playlistById(sync, slot?.playlistId);
  if (!playlist) return null;
  const shuffleBase = syncShuffleBase(sync);

  if (state.playlistId !== playlist.id) {
    state.rotation = buildRotation(playlist, shuffleBase);
    state.index = 0;
    state.playlistId = playlist.id;
    state.anchor = createRotationAnchor();
    // A REBUILT rotation under a sync timeline must resume at the timeline's
    // active segment, not at rotation[0]. Boundary transitions null the
    // playlist id and advance — consuming rotation[0] here replayed the
    // window's first track from 0:00 mid-window on every routine boundary
    // tick (observed live, ~every 200s), knocking the player off the shared
    // timeline with no way back.
    if (hasSyncTimeline(sync)) {
      const alignment = alignMusicToTimeline(
        sync,
        state.rotation.map((item) => item.id),
        now,
      );
      if (alignment) state.index = alignment.rotationIndex;
    }
  }
  const rotation = state.rotation;
  if (rotation.length === 0) return null;
  if (!hasSyncTimeline(sync)) {
    state.index = anchorRotationIndex(rotation, slot, state.index, state.anchor);
  }
  if (state.index >= rotation.length) state.index = 0;
  const item = rotation[state.index];
  state.index += 1;
  return makeEntry(item, "music", slot, playlist, false);
}

/**
 * Position the music rotation on the multi-room sync timeline: rebuild the
 * rotation if the scheduled playlist changed (WITHOUT resetting the anchor —
 * the timeline owns the index), then align index + in-track seek to the
 * active server segment. Returns null when there is no timeline, no active
 * playlist slot, or no active segment; `state` is only mutated on the paths
 * that found a playlist (mirroring the original hook behavior).
 */
export function alignMusicRotationToTimeline(
  sync: DeviceSync | null,
  state: MusicRotationState,
  now?: Date,
): TimelineAlignment | null {
  if (!sync || !hasSyncTimeline(sync)) return null;

  const slot = channelSlot(sync, "music");
  if (!isSlotActive(slot) || slot?.mediaAssetId) return null;

  const playlist = playlistById(sync, slot?.playlistId);
  if (!playlist) return null;

  const shuffleBase = syncShuffleBase(sync);
  if (state.playlistId !== playlist.id) {
    state.rotation = buildRotation(playlist, shuffleBase);
    state.playlistId = playlist.id;
  }

  const alignment = alignMusicToTimeline(
    sync,
    state.rotation.map((item) => item.id),
    now,
  );
  if (!alignment) return null;

  state.index = alignment.rotationIndex;
  return alignment;
}

/**
 * Pull the next entry for a one-shot audio channel (ads / announcements) as a
 * standalone interrupt. CB-015: these have no sync-group ordering to protect,
 * so they consume the shared pickRotationItem-backed fallback directly via
 * `nextRotationItem` rather than a materialized buildRotation array.
 */
export function takeOneShotEntry(
  sync: DeviceSync | null,
  channelType: "ads" | "announcements",
  state: LocalRotationState,
): QueueEntry | null {
  const reason = channelType === "ads" ? "ad" : "announcement";
  const slot = channelSlot(sync, channelType);
  if (!isSlotActive(slot)) return null;

  if (slot?.mediaAssetId) {
    const item = mediaAssetById(sync, slot.mediaAssetId);
    if (!item?.url) return null;
    return makeEntry(item, reason, slot, null, true);
  }

  const playlist = playlistById(sync, slot?.playlistId);
  if (!playlist) return null;
  const shuffleBase = syncShuffleBase(sync);
  const item = nextRotationItem(playlist, slot, shuffleBase, state);
  if (!item) return null;
  return makeEntry(item, reason, slot, playlist, true);
}

/** Decision for one schedule-driven announcement-takeover evaluation. */
export type AnnouncementTakeoverPlan =
  /** Slot inactive: clear the recorded occupancy, queue nothing. */
  | { kind: "inactive" }
  /** Same slot occupancy as last time: nothing to do. */
  | { kind: "unchanged" }
  /** New occupancy but no playable items: record it, queue nothing. */
  | { kind: "empty"; slotKey: string | null }
  /** New occupancy: queue these entries at the front (tone included). */
  | { kind: "queue"; slotKey: string | null; entries: QueueEntry[] };

/**
 * The schedule-driven announcement takeover: when the announcements slot's
 * occupancy changes, the whole rotation is bulk-queued as interrupts, with
 * the attention tone once at the head of the takeover — never before every
 * item, and never doubled when a resync refills the queue mid-block.
 */
export function planAnnouncementTakeover(args: {
  sync: DeviceSync | null;
  /** Slot key (playlistId ?? mediaAssetId) recorded on the last evaluation. */
  previousSlotKey: string | null;
  /** Item id currently on the audio bus (tone dedupe guard). */
  currentItemId: string | null | undefined;
  /** Item id at the head of the interrupt queue (tone dedupe guard). */
  queueHeadItemId: string | null | undefined;
}): AnnouncementTakeoverPlan {
  const { sync } = args;
  const slot = channelSlot(sync, "announcements");
  if (!isSlotActive(slot)) return { kind: "inactive" };

  const slotKey = slot?.playlistId ?? slot?.mediaAssetId ?? null;
  if (args.previousSlotKey === slotKey) return { kind: "unchanged" };

  const shuffleBase = syncShuffleBase(sync);
  let items: DeviceMediaItem[] = [];
  if (slot?.mediaAssetId) {
    const item = mediaAssetById(sync, slot.mediaAssetId);
    if (item) items = [item];
  } else {
    const playlist = playlistById(sync, slot?.playlistId);
    items = playlist ? buildRotation(playlist, shuffleBase) : [];
  }
  if (items.length === 0) return { kind: "empty", slotKey };

  const playlist = playlistById(sync, slot?.playlistId);
  const entries = items.map((it) =>
    makeEntry(it, "announcement", slot, playlist, true),
  );
  const tone = readAnnouncementTone(sync?.device?.settings);
  const toneSrc = resolveToneUrl(
    sync?.device?.settings,
    sync?.device?.announcementToneUrl,
  );
  if (
    tone &&
    toneSrc &&
    shouldPrependTone(tone, args.currentItemId, args.queueHeadItemId)
  ) {
    entries.unshift(toneQueueEntry(tone, toneSrc));
  }
  return { kind: "queue", slotKey, entries };
}
